import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavbarDashbord } from './navbarDashbord';
import { Plus, Edit2, Trash2, Shield, UserX, UserCheck, X, User } from 'lucide-react';
import { getDataComptes, addCompte, updateCompte, deleteCompte } from './action';
import '../styles/admin.css';

export function PageComptes() {
    const dispatch = useDispatch();
    const comptes = useSelector(state => state.comptes) || [];

    useEffect(() => {
        if (comptes.length === 0) {
            dispatch(getDataComptes());
        }
    }, [dispatch, comptes.length]);

    // Filtering States
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [statusFilter, setStatusFilter] = useState('Tous les statuts');

    // UI States
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newAccount, setNewAccount] = useState({ username: '', email: '', role: 'Client' });

    // Helper to parse date strings (handles both YYYY-MM-DD and DD/MM/YYYY)
    const parseDateStr = (dateStr) => {
        if (!dateStr) return new Date(0);

        // If it contains '/', assume DD/MM/YYYY
        if (dateStr.includes('/')) {
            const [day, month, year] = dateStr.split('/');
            return new Date(`${year}-${month}-${day}`);
        }

        // Otherwise assume YYYY-MM-DD or standard JS format
        return new Date(dateStr);
    };

    // Filtering Logic
    const filteredComptes = comptes.filter(c => {
        const cDate = parseDateStr(c.date);
        cDate.setHours(0, 0, 0, 0);

        const start = startDate ? new Date(startDate) : null;
        if (start) start.setHours(0, 0, 0, 0);

        const end = endDate ? new Date(endDate) : null;
        if (end) end.setHours(0, 0, 0, 0);

        // Date Logic
        if (start && end) {
            // Range
            if (cDate < start || cDate > end) return false;
        } else if (start) {
            // Only start -> Exact match
            if (cDate.getTime() !== start.getTime()) return false;
        } else if (end) {
            // Only end -> Exact match
            if (cDate.getTime() !== end.getTime()) return false;
        }

        // Status filter
        if (statusFilter !== 'Tous les statuts' && c.status !== statusFilter) return false;

        return true;
    });

    const handleAddAccount = (e) => {
        e.preventDefault();
        const accountData = {
            ...newAccount,
            date: new Date().toLocaleDateString('fr-FR'), // DD/MM/YYYY
            status: 'Actif'
        };
        dispatch(addCompte(accountData));
        setNewAccount({ username: '', email: '', role: 'Client' });
        setIsAddModalOpen(false);
    };

    const toggleBlock = (compte) => {
        const newStatus = compte.status === 'Bloqué' ? 'Actif' : 'Bloqué';
        dispatch(updateCompte({ ...compte, status: newStatus }));
    };

    const changeStatus = (compte) => {
        const cycle = ['Actif', 'Désactivé', 'Bloqué'];
        const currentIndex = cycle.indexOf(compte.status);
        const nextIndex = (currentIndex + 1) % cycle.length;
        dispatch(updateCompte({ ...compte, status: cycle[nextIndex] }));
    };

    const reactivateAccount = (compte) => {
        dispatch(updateCompte({ ...compte, status: 'Actif' }));
    };

    const handleSupprimer = (id) => {
        if (window.confirm('Voulez-vous vraiment supprimer ce compte ?')) {
            dispatch(deleteCompte(id));
        }
    };

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Gestion des Comptes</h1>
                        <p>Contrôlez les accès utilisateurs et les permissions ({filteredComptes.length} trouvés).</p>
                    </div>
                </header>

                <div className="admin-table-container">
                    <div className="table-header-actions" style={{ flexWrap: 'wrap', gap: '15px' }}>
                        <div className="admin-filters" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="date-range-group" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>De:</span>
                                <input type="date" className="admin-select" value={startDate} onChange={e => setStartDate(e.target.value)} />
                                <span style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>À:</span>
                                <input type="date" className="admin-select" value={endDate} onChange={e => setEndDate(e.target.value)} />
                                {(startDate || endDate) && <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-danger)' }}><X size={14} /></button>}
                            </div>
                            <select className="admin-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                                <option>Tous les statuts</option>
                                <option>Actif</option>
                                <option>Désactivé</option>
                                <option>Bloqué</option>
                            </select>
                        </div>
                        <button className="admin-btn-add" onClick={() => setIsAddModalOpen(true)}>
                            <Plus size={20} />
                            <span>Ajouter un Compte</span>
                        </button>
                    </div>

                    <table className="account-table">
                        <thead>
                            <tr>
                                <th>Nom complet</th>
                                <th>Role</th>
                                <th>Email</th>
                                <th>Mot de passe</th>
                                <th>Inscription</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredComptes.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(15, 23, 42, 0.05)', display: 'flex', alignItems: 'center', justifySelf: 'center', justifyContent: 'center' }}>
                                                <User size={16} color="var(--admin-primary)" />
                                            </div>
                                            <span style={{ fontWeight: '800', color: 'var(--admin-primary)' }}>{c.username}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{ fontSize: '0.7rem', fontWeight: '900', color: c.role && c.role.toLowerCase() === 'admin' ? 'var(--admin-secondary)' : 'var(--admin-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }} className='role'>
                                            {c.role}
                                        </span>
                                    </td>
                                    <td>{c.email}</td>
                                    <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)', fontFamily: 'monospace' }}>{c.password}</td>
                                    <td style={{ fontSize: '0.85rem' }}>{c.date}</td>
                                    <td>
                                        <span
                                            className={`account-badge ${c.status === 'Actif' ? 'account-badge-success' : c.status === 'Désactivé' ? 'account-badge-warning' : 'account-badge-danger'}`}
                                            onClick={() => changeStatus(c)}
                                            style={{ cursor: 'pointer' }}
                                            title="Cliquez pour changer le cycle de statut"
                                        >
                                            {c.status}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="account-action-btns">
                                            <button
                                                className={`account-btn-icon ${c.status === 'Bloqué' ? 'account-btn-danger' : ''}`}
                                                title={c.status === 'Bloqué' ? "Débloquer" : "Bloquer"}
                                                onClick={() => toggleBlock(c)}
                                            >
                                                {c.status === 'Bloqué' ? <UserCheck size={16} /> : <Shield size={16} />}
                                            </button>
                                            <button className="account-btn-icon account-btn-modify" onClick={() => changeStatus(c)} title="Modifier Statut"><Edit2 size={16} /></button>
                                            <button className="account-btn-icon account-btn-delete" onClick={() => handleSupprimer(c.id)} title="Supprimer Account"><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {filteredComptes.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: 'var(--admin-text-muted)' }}>Aucun compte correspondant.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Account Add Modal */}
                {isAddModalOpen && (
                    <div className="admin-modal-overlay" onClick={() => setIsAddModalOpen(false)}>
                        <div className="admin-modal-content" style={{ maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><Plus /> Nouveau Compte</h2>
                                <button className="modal-close-btn" onClick={() => setIsAddModalOpen(false)}>&times;</button>
                            </div>
                            <form className="modal-body" onSubmit={handleAddAccount} style={{ padding: '0px' }}>
                                <div style={{ padding: '20px' }}>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label>Nom d'utilisateur</label>
                                        <input type="text" placeholder="Ex: JeanDupont" value={newAccount.username} onChange={e => setNewAccount({ ...newAccount, username: e.target.value })} required />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                        <label>Adresse E-mail</label>
                                        <input type="email" placeholder="jean@example.com" value={newAccount.email} onChange={e => setNewAccount({ ...newAccount, email: e.target.value })} required />
                                    </div>

                                    <div className="form-group">
                                        <label>Type d'utilisateur (Rôle)</label>
                                        <div className="radio-group-modern">
                                            <div
                                                className={`radio-card ${newAccount.role === 'Admin' ? 'active' : ''}`}
                                                onClick={() => setNewAccount(prev => ({ ...prev, role: 'Admin' }))}
                                            >
                                                <strong>Administrateur</strong>
                                                <p>Accès total au dashboard</p>
                                            </div>
                                            <div
                                                className={`radio-card ${newAccount.role === 'Client' ? 'active' : ''}`}
                                                onClick={() => setNewAccount(prev => ({ ...prev, role: 'Client' }))}
                                            >
                                                <strong>Client standard</strong>
                                                <p>Consultation et commandes</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <button type="submit" className="admin-btn-save" style={{ width: 'calc(100% - 40px)', margin: '0 20px 20px', justifyContent: 'center' }}>
                                    Créer le compte
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
